import React, { memo, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styles from './styles.module.css';
import { getSupplierById, updateSupplier } from '../../actions';
import { useNavigate, useParams } from 'react-router-dom';
import { showAlert } from '../../../../../common/actions';
import { Button, Input } from '../../../../../common/ui';
import PropTypes from 'prop-types';

const UpdateSupplier = memo(
  ({
    suppliersState: { searchByIdSupplierData },
    getSupplierById,
    updateSupplier,
    showAlert,
  }) => {
    const { id } = useParams();
    const [isLoading, setLoading] = useState(false);
    const [companyNameError, setCompanyNameError] = useState('');
    const [addressError, setAddressError] = useState('');
    const [contactNoError, setContactNoError] = useState('');
    const navigate = useNavigate();
    const [updateForm, setUpdateForm] = useState({
      companyname: '',
      address: '',
      contact_no: '',
    });

    useEffect(() => {
      if (id) getSupplierById(id);
    }, [id, getSupplierById]);

    useEffect(() => {
      if (searchByIdSupplierData) {
        setUpdateForm({
          companyname: searchByIdSupplierData.companyname ?? '',
          address: searchByIdSupplierData.address ?? '',
          contact_no: searchByIdSupplierData.contact_no ?? '',
        });
      }
    }, [searchByIdSupplierData]);

    const handleSubmit = async (e) => {
      e.preventDefault();
      const companyname = (updateForm.companyname ?? '').trim();
      const address = (updateForm.address ?? '').trim();
      const contact_no = (updateForm.contact_no ?? '').trim();

      let hasError = false;
      if (!companyname) {
        setCompanyNameError('Please enter the company name.');
        hasError = true;
      } else setCompanyNameError('');
      if (!address) {
        setAddressError('Please enter the company address.');
        hasError = true;
      } else setAddressError('');
      if (!contact_no) {
        setContactNoError('Please enter the company contact number.');
        hasError = true;
      } else setContactNoError('');

      if (hasError) return;

      setLoading(true);
      const isSuccess = await updateSupplier(id, {
        ...updateForm,
        companyname,
        address,
        contact_no,
      });
      setLoading(false);
      if (isSuccess) {
        showAlert({
          isAlertOpen: true,
          alertType: 'success',
          alertMessage: 'Supplier updated successfully.',
        });
        navigate('/inventory/suppliers');
      } else {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: 'Operation failed. Please try again later.',
        });
      }
    };

    const handleCancel = () => {
      navigate('/inventory/suppliers');
    };

    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <h1 className={styles.pageTitle}>Update Supplier</h1>
        </header>
        <div className={styles.body}>
          <form onSubmit={handleSubmit} noValidate className={styles.formCard}>
            <div className={styles.formSection}>
              <div className={styles.fieldGroup}>
                <Input label="ID" type="text" value={id ?? ''} disabled />
              </div>
              <div className={styles.fieldGroup}>
                <Input
                  label="Company Name"
                  type="text"
                  value={updateForm.companyname}
                  onChange={(value) => {
                    setUpdateForm((prev) => ({ ...prev, companyname: value }));
                    if (companyNameError) setCompanyNameError('');
                  }}
                  placeholder="Company name"
                  error={companyNameError}
                />
              </div>
              <div className={styles.fieldGroup}>
                <Input
                  label="Address"
                  type="text"
                  value={updateForm.address}
                  onChange={(value) => {
                    setUpdateForm((prev) => ({ ...prev, address: value }));
                    if (addressError) setAddressError('');
                  }}
                  placeholder="Address"
                  error={addressError}
                />
              </div>
              <div className={styles.fieldGroup}>
                <Input
                  label="Contact No"
                  type="text"
                  value={updateForm.contact_no}
                  onChange={(value) => {
                    setUpdateForm((prev) => ({ ...prev, contact_no: value }));
                    if (contactNoError) setContactNoError('');
                  }}
                  placeholder="Contact number"
                  error={contactNoError}
                />
              </div>
            </div>
            <div className={styles.buttonPanel}>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Loading...' : 'Submit'}
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }
);

UpdateSupplier.propTypes = {
  suppliersState: PropTypes.shape({
    searchByIdSupplierData: PropTypes.object,
  }).isRequired,
  getSupplierById: PropTypes.func.isRequired,
  updateSupplier: PropTypes.func.isRequired,
  showAlert: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  suppliersState: state.suppliersState,
});

export default connect(mapStateToProps, {
  getSupplierById,
  updateSupplier,
  showAlert,
})(UpdateSupplier);
